// Banner create/edit dialog body.
//
// Rendered as the `content` of the shared ActionDialog. Sections in
// order: severity → per-locale message tabs → schedule → CTA →
// dismissibility → audience. Submitting opens a publish-confirm
// modal first so the operator can sanity-check the dedup-reach
// number before POSTing.

import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Button, Stack } from 'components'
import { useCreateBanner } from 'services/queries/use-create-banner'
import { useUpdateBanner } from 'services/queries/use-update-banner'
import {
  BannerAudienceT,
  BannerRoleT,
  BannerSeverityT,
  BannerT,
  CreateBannerDataT,
} from 'types/global'
import AudienceSelector, { useAudienceSummary } from './audience-selector'
import PublishConfirmDialog from './publish-confirm-dialog'
import {
  defaultEndsAtFromStartsAt,
  isInvalidCtaUrl,
  localeTabs,
} from './helpers'

interface BannerDialogProps {
  closeFn: () => void
  // Editing? Pass the banner — the form pre-fills and the submit
  // action becomes a PUT. Undefined means create.
  banner?: BannerT
}

// Converts an ISO timestamp to the `YYYY-MM-DDTHH:mm` shape that the
// native `<input type="datetime-local">` consumes (no seconds, no
// timezone suffix).
const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`
}

const localInputToIso = (local: string): string | null => {
  if (!local) return null
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

const SEVERITIES: BannerSeverityT[] = ['info', 'warning', 'error', 'success']

const initialAudience = (banner?: BannerT): BannerAudienceT => {
  if (banner) {
    return {
      target_all: banner.target_all,
      target_group_ids: banner.target_group_ids,
      target_customer_ids: banner.target_customer_ids,
      target_roles: banner.target_roles,
    }
  }
  return {
    target_all: true,
    target_group_ids: [],
    target_customer_ids: [],
    target_roles: [],
  }
}

const BannerDialog: React.FC<BannerDialogProps> = ({ closeFn, banner }) => {
  const isEdit = Boolean(banner)

  const [severity, setSeverity] = useState<BannerSeverityT>(
    banner?.severity ?? 'info',
  )

  // Per-locale message + cta-label fields.
  const [messages, setMessages] = useState<Record<string, string>>({
    en: banner?.message_en ?? '',
    da: banner?.message_da ?? '',
    nb: banner?.message_nb ?? '',
    sv: banner?.message_sv ?? '',
  })
  const [ctaLabels, setCtaLabels] = useState<Record<string, string>>({
    en: banner?.cta_label_en ?? '',
    da: banner?.cta_label_da ?? '',
    nb: banner?.cta_label_nb ?? '',
    sv: banner?.cta_label_sv ?? '',
  })
  // Per-language CTA URLs (ENG2-2106) — the only URL shape. An operator
  // who wants one link for everyone pastes it into all four tabs.
  const [ctaUrls, setCtaUrls] = useState<Record<string, string>>({
    en: banner?.cta_url_en ?? '',
    da: banner?.cta_url_da ?? '',
    nb: banner?.cta_url_nb ?? '',
    sv: banner?.cta_url_sv ?? '',
  })

  const [localeIdx, setLocaleIdx] = useState(0)

  // Schedule — stored as ISO strings, edited as datetime-local.
  const initialStartsAt =
    banner?.starts_at ?? new Date(Date.now() + 60 * 60 * 1000).toISOString()
  const initialEndsAt =
    banner?.ends_at ?? defaultEndsAtFromStartsAt(initialStartsAt)

  const [startsAt, setStartsAt] = useState(initialStartsAt)
  const [endsAt, setEndsAt] = useState<string | null>(initialEndsAt)

  const [dismissible, setDismissible] = useState<boolean>(
    banner?.dismissible ?? true,
  )

  const [audience, setAudience] = useState<BannerAudienceT>(() =>
    initialAudience(banner),
  )
  const audienceSummary = useAudienceSummary(audience)

  const [confirmOpen, setConfirmOpen] = useState(false)

  const createMutation = useCreateBanner()
  const updateMutation = useUpdateBanner()
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading

  // Spec: error banners cannot be dismissed by customers — pin the
  // toggle to false and disable the switch. Server-side validation
  // mirrors this so the row is safe even if a client bypasses the UI.
  const effectiveDismissible = severity === 'error' ? false : dismissible
  const dismissibleLocked = severity === 'error'

  // Every locale message body is required — the dashboard has no
  // fallback path, so a banner can't ship with a missing locale.
  const enRequiredEmpty = messages.en.trim().length === 0
  const daRequiredEmpty = messages.da.trim().length === 0
  const nbRequiredEmpty = messages.nb.trim().length === 0
  const svRequiredEmpty = messages.sv.trim().length === 0
  const anyMessageEmpty =
    enRequiredEmpty || daRequiredEmpty || nbRequiredEmpty || svRequiredEmpty

  // If the operator gives the banner CTA URLs, every locale CTA label
  // becomes required too — the dashboard renders the button in the
  // customer's locale with no fallback. Without URLs the labels are
  // ignored.
  const ctaUrlsTrimmed = {
    en: ctaUrls.en.trim(),
    da: ctaUrls.da.trim(),
    nb: ctaUrls.nb.trim(),
    sv: ctaUrls.sv.trim(),
  }
  // "The banner has a CTA" — true once any per-language URL is typed.
  const ctaUrlSet =
    ctaUrlsTrimmed.en !== '' ||
    ctaUrlsTrimmed.da !== '' ||
    ctaUrlsTrimmed.nb !== '' ||
    ctaUrlsTrimmed.sv !== ''

  // Per-language URLs are all-or-nothing (CMAPI rejects a partial
  // set): once any URL is typed, the other three become required.
  const perLangUrlEmpty = {
    en: ctaUrlsTrimmed.en === '',
    da: ctaUrlsTrimmed.da === '',
    nb: ctaUrlsTrimmed.nb === '',
    sv: ctaUrlsTrimmed.sv === '',
  }
  const perLangUrlsIncomplete =
    ctaUrlSet &&
    (perLangUrlEmpty.en ||
      perLangUrlEmpty.da ||
      perLangUrlEmpty.nb ||
      perLangUrlEmpty.sv)
  const perLangUrlInvalid = {
    en: isInvalidCtaUrl(ctaUrls.en),
    da: isInvalidCtaUrl(ctaUrls.da),
    nb: isInvalidCtaUrl(ctaUrls.nb),
    sv: isInvalidCtaUrl(ctaUrls.sv),
  }
  const anyPerLangUrlInvalid =
    perLangUrlInvalid.en ||
    perLangUrlInvalid.da ||
    perLangUrlInvalid.nb ||
    perLangUrlInvalid.sv
  const ctaLabelEmpty = {
    en: ctaLabels.en.trim() === '',
    da: ctaLabels.da.trim() === '',
    nb: ctaLabels.nb.trim() === '',
    sv: ctaLabels.sv.trim() === '',
  }
  const ctaLabelsIncomplete =
    ctaUrlSet &&
    (ctaLabelEmpty.en ||
      ctaLabelEmpty.da ||
      ctaLabelEmpty.nb ||
      ctaLabelEmpty.sv)

  // Per-locale completion flags. Used to mark the tab itself when its
  // message or (when CTA URL is set) its CTA label is empty — the
  // inputs are hidden on inactive tabs, so without this the operator
  // has no signal which locale is blocking Publish.
  const messageEmptyByLocale: Record<string, boolean> = {
    en: enRequiredEmpty,
    da: daRequiredEmpty,
    nb: nbRequiredEmpty,
    sv: svRequiredEmpty,
  }
  const localeIncomplete: Record<string, boolean> = {
    en:
      messageEmptyByLocale.en ||
      (ctaUrlSet && (ctaLabelEmpty.en || perLangUrlEmpty.en)) ||
      perLangUrlInvalid.en,
    da:
      messageEmptyByLocale.da ||
      (ctaUrlSet && (ctaLabelEmpty.da || perLangUrlEmpty.da)) ||
      perLangUrlInvalid.da,
    nb:
      messageEmptyByLocale.nb ||
      (ctaUrlSet && (ctaLabelEmpty.nb || perLangUrlEmpty.nb)) ||
      perLangUrlInvalid.nb,
    sv:
      messageEmptyByLocale.sv ||
      (ctaUrlSet && (ctaLabelEmpty.sv || perLangUrlEmpty.sv)) ||
      perLangUrlInvalid.sv,
  }

  const startsAtMissing = !startsAt
  const endsAtBeforeStart = useMemo(() => {
    if (!endsAt) return false
    return new Date(endsAt).getTime() <= new Date(startsAt).getTime()
  }, [endsAt, startsAt])

  const canSubmit =
    !anyMessageEmpty &&
    !ctaLabelsIncomplete &&
    !perLangUrlsIncomplete &&
    !anyPerLangUrlInvalid &&
    !startsAtMissing &&
    !endsAtBeforeStart &&
    !audienceSummary.isEmpty &&
    !isSubmitting

  // Human-readable list of what's still blocking Publish. Rendered
  // next to the disabled button so the operator never has to guess
  // which field — particularly which locale — is incomplete.
  const blockingReasons: string[] = []
  const missingMessageLabels = localeTabs
    .filter((t) => messageEmptyByLocale[t.code])
    .map((t) => t.label)
  if (missingMessageLabels.length > 0) {
    blockingReasons.push(`add Message for ${missingMessageLabels.join(', ')}`)
  }
  if (ctaLabelsIncomplete) {
    const missingCtaLabels = localeTabs
      .filter((t) => ctaLabelEmpty[t.code as keyof typeof ctaLabelEmpty])
      .map((t) => t.label)
    blockingReasons.push(`add CTA label for ${missingCtaLabels.join(', ')}`)
  }
  if (perLangUrlsIncomplete) {
    const missingUrlLabels = localeTabs
      .filter((t) => perLangUrlEmpty[t.code as keyof typeof perLangUrlEmpty])
      .map((t) => t.label)
    blockingReasons.push(`add CTA URL for ${missingUrlLabels.join(', ')}`)
  }
  if (anyPerLangUrlInvalid) {
    const invalidUrlLabels = localeTabs
      .filter(
        (t) => perLangUrlInvalid[t.code as keyof typeof perLangUrlInvalid],
      )
      .map((t) => t.label)
    blockingReasons.push(`fix the CTA URL for ${invalidUrlLabels.join(', ')}`)
  }
  if (endsAtBeforeStart) blockingReasons.push('fix Ends at')
  if (audienceSummary.isEmpty) blockingReasons.push('pick an audience')

  const buildPayload = (): CreateBannerDataT => ({
    severity,
    message_en: messages.en.trim(),
    message_da: messages.da.trim(),
    message_nb: messages.nb.trim(),
    message_sv: messages.sv.trim(),
    // When the banner has a CTA we ship trimmed strings for every label —
    // the form guarantees they're non-empty via the disable above. When
    // there's no CTA we ship nulls so the API sees a "no CTA" banner.
    cta_label_en: ctaUrlSet ? ctaLabels.en.trim() : null,
    cta_label_da: ctaUrlSet ? ctaLabels.da.trim() : null,
    cta_label_nb: ctaUrlSet ? ctaLabels.nb.trim() : null,
    cta_label_sv: ctaUrlSet ? ctaLabels.sv.trim() : null,
    // No CTA → explicit nulls so the API sees (and stores) a no-CTA
    // banner; an edit that clears the URLs clears the columns too
    // (CMAPI treats explicit null as a clear).
    cta_url_en: ctaUrlSet ? ctaUrlsTrimmed.en : null,
    cta_url_da: ctaUrlSet ? ctaUrlsTrimmed.da : null,
    cta_url_nb: ctaUrlSet ? ctaUrlsTrimmed.nb : null,
    cta_url_sv: ctaUrlSet ? ctaUrlsTrimmed.sv : null,
    starts_at: startsAt,
    ends_at: endsAt,
    dismissible: effectiveDismissible,
    target_all: audience.target_all,
    target_group_ids: audience.target_group_ids,
    target_customer_ids: audience.target_customer_ids,
    target_roles: audience.target_roles,
  })

  const handlePublishClick = (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setConfirmOpen(true)
  }

  const handleConfirmedSubmit = () => {
    setConfirmOpen(false)
    const payload = buildPayload()
    if (isEdit && banner) {
      updateMutation.mutate(
        { id: banner.id, body: payload },
        {
          onSuccess: (response) => {
            if (response.success) closeFn()
          },
        },
      )
    } else {
      createMutation.mutate(payload, {
        onSuccess: (response) => {
          if (response.success) closeFn()
        },
      })
    }
  }

  const activeLocale = localeTabs[localeIdx]

  return (
    <form onSubmit={handlePublishClick} data-testid="banner-form">
      <Box
        sx={{
          px: 3,
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* -------- Severity -------- */}
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: 'text.secondary',
              mb: 0.75,
            }}
          >
            Severity
          </Typography>
          <ToggleButtonGroup
            value={severity}
            exclusive
            size="small"
            onChange={(_e, next) => {
              if (next) setSeverity(next as BannerSeverityT)
            }}
            data-cy="severity-toggle"
          >
            {SEVERITIES.map((s) => (
              <ToggleButton
                key={s}
                value={s}
                sx={{ textTransform: 'capitalize', px: 2 }}
              >
                {s}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* -------- CTA link (ENG2-2106) --------
            URLs are per-language and live inside the locale tabs, next
            to the message and label, so the operator makes one sweep
            through the tabs filling message + URL + label per locale.
            This explainer sits above the tabs so the all-or-nothing
            rule is visible before the sweep starts. */}
        <Alert severity="info" sx={{ fontSize: 13 }}>
          CTA link is optional and set per language in the tabs below — e.g.
          English users to the English help centre, Danish / Norwegian / Swedish
          users to the Danish one. Paste the same URL into all four tabs to send
          everyone to one place. Once any URL is set, all four are required.
        </Alert>

        <Divider />

        {/* -------- Per-locale messages -------- */}
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            Message
          </Typography>
          <Tabs
            value={localeIdx}
            onChange={(_e, idx) => setLocaleIdx(idx)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2.5 }}
          >
            {localeTabs.map((t) => {
              const incomplete = localeIncomplete[t.code]
              return (
                <Tab
                  key={t.code}
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      {t.label}
                      {incomplete && (
                        <Box
                          component="span"
                          data-testid={`locale-tab-${t.code}-incomplete`}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            display: 'inline-block',
                          }}
                        />
                      )}
                    </Box>
                  }
                  data-cy={`locale-tab-${t.code}`}
                />
              )
            })}
          </Tabs>
          <TextField
            label={`Message (${activeLocale.label})`}
            value={messages[activeLocale.code]}
            onChange={(e) =>
              setMessages((prev) => ({
                ...prev,
                [activeLocale.code]: e.target.value,
              }))
            }
            placeholder="Body shown to customers..."
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            size="small"
            required
            error={
              (activeLocale.code === 'en' && enRequiredEmpty) ||
              (activeLocale.code === 'da' && daRequiredEmpty) ||
              (activeLocale.code === 'nb' && nbRequiredEmpty) ||
              (activeLocale.code === 'sv' && svRequiredEmpty)
            }
            helperText="Required."
          />
          <TextField
            label={`CTA URL (${activeLocale.label})`}
            value={ctaUrls[activeLocale.code]}
            onChange={(e) =>
              setCtaUrls((prev) => ({
                ...prev,
                [activeLocale.code]: e.target.value,
              }))
            }
            placeholder="https://..."
            fullWidth
            size="small"
            sx={{ mt: 1.5 }}
            required={ctaUrlSet}
            error={
              perLangUrlInvalid[
                activeLocale.code as keyof typeof perLangUrlInvalid
              ] ||
              (ctaUrlSet &&
                perLangUrlEmpty[
                  activeLocale.code as keyof typeof perLangUrlEmpty
                ])
            }
            helperText={
              perLangUrlInvalid[
                activeLocale.code as keyof typeof perLangUrlInvalid
              ]
                ? 'Enter a valid http:// or https:// URL with a real domain (e.g. https://risika.com).'
                : ctaUrlSet
                ? 'Required — all four languages need a URL when any is set.'
                : 'Optional. Leave all four languages empty for no CTA.'
            }
            inputProps={{ 'data-cy': `cta-url-${activeLocale.code}` }}
          />
          <TextField
            label={`CTA label (${activeLocale.label})`}
            value={ctaLabels[activeLocale.code]}
            onChange={(e) =>
              setCtaLabels((prev) => ({
                ...prev,
                [activeLocale.code]: e.target.value,
              }))
            }
            placeholder="e.g. Read more"
            fullWidth
            size="small"
            sx={{ mt: 1.5 }}
            // CTA labels are only meaningful when there's a CTA URL. We
            // disable the inputs entirely until the URL is set rather
            // than letting the operator type values that would silently
            // be discarded by buildPayload (which ships nulls when the
            // URL is empty). Existing typed values are preserved in
            // component state so re-enabling the URL brings them back.
            disabled={!ctaUrlSet}
            required={ctaUrlSet}
            error={ctaUrlSet && ctaLabelEmpty[activeLocale.code]}
            helperText={
              ctaUrlSet
                ? 'Required when CTA URL is set.'
                : 'Add a CTA URL above to enable the per-locale labels.'
            }
          />
        </Box>

        <Divider />

        {/* -------- Schedule -------- */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}
          >
            Schedule
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Starts at"
              type="datetime-local"
              value={isoToLocalInput(startsAt)}
              onChange={(e) => {
                const iso = localInputToIso(e.target.value)
                if (iso) setStartsAt(iso)
              }}
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 240 }}
              inputProps={{ 'data-cy': 'starts-at' }}
            />
            <TextField
              label="Ends at"
              type="datetime-local"
              value={isoToLocalInput(endsAt ?? '')}
              onChange={(e) => {
                const iso = localInputToIso(e.target.value)
                setEndsAt(iso)
              }}
              size="small"
              error={endsAtBeforeStart}
              helperText={
                endsAtBeforeStart ? 'Must be after Starts at.' : undefined
              }
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 240 }}
              inputProps={{ 'data-cy': 'ends-at' }}
            />
          </Box>
          {!endsAt && (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              This banner will keep re-appearing daily until you delete it.
              Consider setting an end date.
            </Alert>
          )}
        </Box>

        <Divider />

        {/* -------- Dismissibility -------- */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={effectiveDismissible}
                onChange={(_e, v) => setDismissible(v)}
                disabled={dismissibleLocked}
                data-cy="dismissible-toggle"
              />
            }
            label={
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                Dismissible by customer
              </Typography>
            }
          />
          {dismissibleLocked && (
            <Alert severity="info" sx={{ fontSize: 13, mt: 1 }}>
              Error banners cannot be dismissed by customers — they&apos;re
              pinned until you delete or end the banner.
            </Alert>
          )}
        </Box>

        <Divider />

        {/* -------- Audience -------- */}
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: 'text.secondary',
              mb: 1,
            }}
          >
            Audience
          </Typography>
          <AudienceSelector value={audience} onChange={setAudience} />
        </Box>
      </Box>

      {/* -------- Footer -------- */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        {blockingReasons.length > 0 && (
          <Typography
            data-testid="publish-blockers"
            sx={{
              fontSize: 12,
              color: 'error.main',
              mr: 'auto',
              flex: '1 1 0',
              minWidth: 0,
            }}
          >
            Publish disabled — {blockingReasons.join('; ')}.
          </Typography>
        )}
        <Stack spacing={1}>
          <Button variant="text" onClick={closeFn} data-cy="banner-cancel">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            loading={isSubmitting}
            data-cy="banner-publish"
          >
            {isEdit ? 'Save changes' : 'Publish banner'}
          </Button>
        </Stack>
      </Box>

      <PublishConfirmDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmedSubmit}
        isAllCustomers={audience.target_all}
        reach={audienceSummary.reach}
        roles={audienceSummary.roles}
        isUpdate={isEdit}
      />
    </form>
  )
}

export default BannerDialog

// Re-export so tests importing the role type from this module stay
// stable as the module surface shifts.
export type { BannerRoleT }
